import { useEffect, useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useSEO, useJsonLd } from "../hooks/useSEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const categories = ["Tous", "Plantation", "Paille & Litière", "Culture", "Récolte"];

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Tous");

  useSEO({
    title: "FAQ technique sur le miscanthus | Miscanthus Sarthe",
    description: "Plantation, densité, récolte, paillage, litière : les réponses de notre exploitation sarthoise aux questions fréquentes sur la culture du miscanthus.",
    path: "/faq",
  });

  useJsonLd(
    "jsonld-faq",
    faqs.length === 0
      ? null
      : {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/faq`)
      .then(r => { setFaqs(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = activeCategory === "Tous" ? faqs : faqs.filter(f => f.category === activeCategory);

  return (
    <main data-testid="faq-page" style={{ backgroundColor: "#f5f2ec" }}>
      {/* Header */}
      <div className="page-header" style={{ paddingTop: "7rem" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-4" />
          <h1 className="font-display text-5xl sm:text-6xl mb-5" style={{ color: "#3a2b24" }}>FAQ Technique</h1>
          <p className="text-base max-w-2xl" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            Toutes les réponses à vos questions sur la culture, la plantation et l'utilisation du miscanthus.
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="faq-categories">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-4 py-1.5 rounded-full text-sm font-body font-500 transition-all"
                style={{
                  backgroundColor: activeCategory === cat ? "#2d5016" : "#eae4d8",
                  color: activeCategory === cat ? "white" : "#3a2b24",
                  border: activeCategory === cat ? "1px solid #2d5016" : "1px solid #d8d1c4",
                }}
                data-testid={`faq-category-${cat}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: "#d8d1c4", borderTopColor: "#2d5016" }} />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm py-12" style={{ color: "#3a2b24", opacity: 0.6, fontFamily: "'Satoshi', sans-serif" }}>
              Aucune question dans cette catégorie.
            </p>
          ) : (
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {filtered.map((faq, i) => (
                <AccordionItem
                  key={faq.id || i}
                  value={`faq-${faq.id || i}`}
                  className="rounded-xl overflow-hidden"
                  style={{ border: "1px solid #d8d1c4", backgroundColor: "white" }}
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger
                    className="px-6 py-4 text-left hover:no-underline hover:bg-beige-muted transition-colors"
                    style={{ color: "#3a2b24" }}
                  >
                    <span className="font-display text-lg pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5">
                    <p className="text-sm leading-relaxed" style={{ color: "#3a2b24", opacity: 0.75, fontFamily: "'Satoshi', sans-serif" }}>
                      {faq.answer}
                    </p>
                    {faq.category && (
                      <span className="inline-block mt-3 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "#eae4d8", color: "#b57a3e", fontFamily: "'Satoshi', sans-serif" }}>
                        {faq.category}
                      </span>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Still questions? */}
      <section style={{ backgroundColor: "#eae4d8" }} className="py-14 px-4 text-center">
        <HelpCircle size={36} style={{ color: "#2d5016" }} className="mx-auto mb-4" />
        <h2 className="font-display text-3xl mb-3" style={{ color: "#3a2b24" }}>
          Vous n'avez pas trouvé votre réponse ?
        </h2>
        <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
          Contactez-nous directement. Nous serons ravis de répondre à toutes vos questions sur le miscanthus.
        </p>
        <Link to="/contact" className="btn-primary inline-flex items-center gap-2 px-8 py-3.5 rounded-md font-body font-500 text-base" data-testid="faq-contact-cta">
          Nous poser une question
        </Link>
      </section>
    </main>
  );
}
